"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { X } from "lucide-react";

type ErrorContextType = {
  error: string;
  success: string;
  setError: (msg: string) => void;
  setSuccess: (msg: string) => void;
  clearError: () => void;
  clearSuccess: () => void;
};

const ErrorContext = createContext<ErrorContextType | null>(null);

export function ErrorProvider({ children }: { children: ReactNode }) {
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const clearError = () => setError("");
  const clearSuccess = () => setSuccess("");

  // Auto-close after 5 seconds
  useEffect(() => {
    if (!error) return;

    const timer = setTimeout(() => {
      clearError();
    }, 5000); // 5000ms = 5 seconds

    // Clean up if error changes or component unmounts
    return () => clearTimeout(timer);
  }, [error]);
  useEffect(() => {
    if (!success) return;

    const timer = setTimeout(() => {
      clearSuccess();
    }, 5000);

    return () => clearTimeout(timer);
  }, [success]);

  return (
    <ErrorContext.Provider value={{ error, success, setError, setSuccess, clearError, clearSuccess }} >
      {children}

      {/* GLOBAL TOP ALERT */}
      {error && (
        <div className="fixed inset-x-0 top-4 z-[9999] flex justify-center pointer-events-none">
          <div className="pointer-events-auto flex items-center gap-3 bg-red-500 text-white px-5 py-3 rounded-md shadow-xl max-w-lg w-fit">
            <span className="text-sm font-medium">{error}</span>

            <button
              onClick={clearError}
              className="ml-2 hover:opacity-80"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
      {success && (
        <div className="fixed inset-x-0 top-16 z-[9999] flex justify-center pointer-events-none">
          <div className="pointer-events-auto flex items-center gap-3 bg-green-500 text-white px-5 py-3 rounded-md shadow-xl max-w-lg w-fit">
            <span className="text-sm font-medium">{success}</span>

            <button
              onClick={clearSuccess}
              className="ml-2 hover:opacity-80"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </ErrorContext.Provider>
  );
}

export function useGlobalError() {
  const ctx = useContext(ErrorContext);
  if (!ctx) {
    throw new Error("useGlobalError must be used inside ErrorProvider");
  }
  return ctx;
}
